/**
 * Authentication Store
 *
 * Zustand store for managing authentication state ONLY.
 * Token lifecycle is managed by Supabase client.
 * Tenant and environment management handled by their respective providers.
 */

import type { AuthUser, LoginResponse } from '@monorepo/shared/schemas/auth'
import { create } from 'zustand'
import { resetLogoutGuard } from '@/lib/api-client'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { supabase } from '@/shared/lib/supabase/client'
import { authService } from '../services/authService'

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  hasInitialized: boolean
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  forceLogout: () => void
  clearError: () => void
  setAuthFromOAuth: (result: LoginResponse) => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>(set => ({
  // Initial state — isLoading: true blocks children until INITIAL_SESSION fires
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hasInitialized: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await authService.login({ email, password })

      // Sync session to Supabase client (enables auto-refresh, persistence)
      try {
        await supabase.auth.setSession({
          access_token: response.tokens.access_token,
          refresh_token: response.tokens.refresh_token,
        })
      } catch {
        // setSession() makes a network call — if it fails, login fails
        set({ isLoading: false, error: 'Failed to establish session' })
        throw new Error('Failed to establish session')
      }

      // Reset the logout guard so future 401s can trigger forceLogout
      resetLogoutGuard()

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: extractErrorMessage(error),
      })
      throw error
    }
  },

  signup: async (email: string, password: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await authService.signup({ email, password })

      // Sync session to Supabase if tokens are provided
      if (response.tokens) {
        try {
          await supabase.auth.setSession({
            access_token: response.tokens.access_token,
            refresh_token: response.tokens.refresh_token,
          })
        } catch {
          set({ isLoading: false, error: 'Failed to establish session' })
          throw new Error('Failed to establish session')
        }
      }

      resetLogoutGuard()

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: extractErrorMessage(error),
      })
      throw error
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null })

    try {
      await authService.logout()
    } catch {
      // Logout API failure is non-critical
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    }
  },

  forceLogout: () => {
    // Re-entrancy guard — prevent cascading logouts
    const state = useAuthStore.getState()
    if (!state.isAuthenticated) return

    // Immediately mark as logged out (prevents concurrent triggers)
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    })

    // Clear React Query cache to prevent stale queries during redirect
    // Dynamic import to avoid circular dependency
    import('@/shared/lib/queryClient').then(({ queryClient }) => {
      queryClient.clear()
    })

    // Clear Supabase session
    supabase.auth.signOut().catch(() => {})

    // Clear app context
    localStorage.removeItem('current_tenant_id')
    localStorage.removeItem('current_environment_id')
    localStorage.removeItem('currentTenant')
    localStorage.removeItem('coobird_auth_token')
    localStorage.removeItem('coobird_refresh_token')

    // Hard redirect (resets all in-memory state: React Query cache, contexts, etc.)
    const currentPath = window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
  },

  clearError: () => {
    set({ error: null })
  },

  setAuthFromOAuth: (result: LoginResponse) => {
    resetLogoutGuard()
    set({
      user: result.user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    })
  },
}))

// Export types
export type { AuthUser as User }
